import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserModel } from '@app/data';
import { BaseService } from '@app/services';
import { DirectivesModule, SharedModule } from '@app/shared';
import { environment } from '@env/environment';
import { RxwebValidators } from '@rxweb/reactive-form-validators';
import { Subscription } from 'rxjs';
import { AuthService, ISignin } from '..';
import { AuthShellComponent } from '../shell';
import { FormGroupInterface, FormGroupValuesInterface } from './signin.interface';

declare const window: any;

@Component({
  imports: [SharedModule, DirectivesModule, AuthShellComponent],
  selector: 'app-signin',
  templateUrl: './signin.component.html',
})
export class SigninComponent extends BaseService implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);

  params$: Subscription;
  user: UserModel = new UserModel();
  formSignin: FormGroup;
  returnUrl: string;

  ngOnInit(): void {
    this.params$ = this.route.queryParams.subscribe(this.onParamsChange.bind(this));
    this.createFormSignin();
    this.initGoogleAuth();
    this.initFacebookAuth();
  }

  ngOnDestroy(): void {
    this.params$.unsubscribe();
  }

  onParamsChange(params: { returnUrl?: string }): void {
    this.returnUrl = params?.returnUrl ?? '/dashboard';
  }

  createFormSignin(): void {
    this.formSignin = new FormGroup<FormGroupInterface>({
      email: new FormControl('', [RxwebValidators.required(), RxwebValidators.email()]),
      password: new FormControl('', [RxwebValidators.required()]),
    });
  }

  signin(): void {
    const formValues: FormGroupValuesInterface = this.formSignin.getRawValue();
    const { email, password } = formValues;
    this._spin.start();
    const params: ISignin = {
      email: email,
      password: password,
    };
    this.authService
      .signin(params)
      .then(() => {
        this._toast.success({ message: 'Login realizado com sucesso!' });
        void this.router.navigateByUrl(this.returnUrl);
      })
      .catch((e: Error) => {
        this._toast.error({ message: e.message });
      })
      .finally(() => {
        this._spin.stop();
      });
  }

  initGoogleAuth(): void {
    if (!environment.social.googleClientId) return;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      window.google.accounts.id.initialize({
        client_id: environment.social.googleClientId,
        callback: this.handleGoogleCredentialResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(document.getElementById('google-btn-container'), {
        theme: 'outline',
        size: 'large',
        type: 'standard',
        text: 'signin_with',
        width: 240,
        shape: 'rectangular',
      });
    };
    document.head.appendChild(script);
  }

  handleGoogleCredentialResponse(response: { credential?: string }): void {
    if (response.credential) {
      this._spin.start();
      this.authService
        .socialLoginGoogle(response.credential)
        .then(() => {
          this._toast.success({ message: 'Login realizado com sucesso!' });
          void this.router.navigateByUrl(this.returnUrl);
        })
        .catch((e: Error) => {
          this._toast.error({ message: e.message });
        })
        .finally(() => {
          this._spin.stop();
        });
    }
  }

  initFacebookAuth(): void {
    if (!environment.social.facebookAppId) return;
    window.fbAsyncInit = function () {
      window.FB.init({
        appId: environment.social.facebookAppId,
        cookie: true,
        xfbml: true,
        version: 'v18.0',
      });
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  loginWithFacebook(): void {
    if (!window.FB) {
      this._toast.error({ message: 'Facebook SDK não carregado' });
      return;
    }
    window.FB.login(
      (response: { authResponse?: { accessToken?: string } }) => {
        if (response.authResponse && response.authResponse.accessToken) {
          this._spin.start();
          this.authService
            .socialLoginFacebook(response.authResponse.accessToken)
            .then(() => {
              this._toast.success({ message: 'Login realizado com sucesso!' });
              void this.router.navigateByUrl(this.returnUrl);
            })
            .catch((e: Error) => {
              this._toast.error({ message: e.message });
            })
            .finally(() => {
              this._spin.stop();
            });
        } else {
          this._toast.error({ message: 'Falha ao autenticar com o Facebook' });
        }
      },
      { scope: 'public_profile,email' }
    );
  }
}
